import React from 'react';
import { Alert, Card, Spinner } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import type { MessageBoardFeed } from '../hooks/useMessageBoardFeed';
import MessageBoardPostCard from './MessageBoardPostCard';

export interface MessageBoardListProps {
  feed: MessageBoardFeed;
  onRemoved?: () => void;
}

const MessageBoardList: React.FC<MessageBoardListProps> = ({ feed, onRemoved }) => {
  const { hasPermission } = usePermissions();
  const canModerate = hasPermission('message_board:moderate');
  const canViewReads = hasPermission('message_board:view_reads');
  const {
    foremanAreas,
    selectedAreaName,
    hasExtraFilters,
    loading,
    error,
    setError,
    result,
    page,
    setPage,
  } = feed;

  if (foremanAreas.length === 0) {
    return (
      <Alert variant="warning">
        You have no foreman areas assigned, so there are no sent notifications to show.
      </Alert>
    );
  }

  const recentCount = result?.recentCount ?? 0;

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl">
        <Card.Header className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-border-b-0 tw-bg-surface tw-px-4 tw-py-3">
          <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
            Sent notifications
          </p>
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            {recentCount} in the last 7 days
          </p>
        </Card.Header>
        {loading && !result ? (
          <Card.Body className="tw-flex tw-items-center tw-gap-2 tw-text-content-secondary">
            <Spinner size="sm" />
            <span>Loading notifications…</span>
          </Card.Body>
        ) : error && !result ? (
          <Card.Body>
            <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Could not load notifications.</p>
          </Card.Body>
        ) : !result || result.totalCount === 0 ? (
          <Card.Body>
            <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
              {hasExtraFilters
                ? 'No notifications match these filters.'
                : `No notifications have been sent in ${selectedAreaName}.`}
            </p>
          </Card.Body>
        ) : (
          <div className={`tw-min-h-0 tw-flex-1 tw-overflow-y-auto ${loading ? 'tw-opacity-60' : ''}`}>
            {result.data.map((post) => (
              <MessageBoardPostCard
                key={post.id}
                post={post}
                canModerate={canModerate}
                canViewReads={canViewReads}
                onRemoved={onRemoved}
              />
            ))}
          </div>
        )}
        {result && result.totalPages > 1 && (
          <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-t tw-border-line tw-px-4 tw-py-2 tw-text-sm">
            <button
              type="button"
              className="tw-min-h-11 tw-text-content-secondary disabled:tw-opacity-40"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span className="tw-tabular-nums tw-text-content-muted">
              {result.page} / {result.totalPages}
            </span>
            <button
              type="button"
              className="tw-min-h-11 tw-text-content-secondary disabled:tw-opacity-40"
              disabled={!result.hasMore || loading}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MessageBoardList;
